import { FormEvent, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import GroupIcon from '@mui/icons-material/Group'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { ORG_FEATURES, ORG_FEATURE_LABELS } from 'chat-shared'
import type {
  AssignmentAccess,
  Group,
  GroupMember,
  McpAuthMode,
  McpServerStatus,
  McpServerSummary,
  McpTransport,
  OrganizationMember,
  OrgFeature,
  OrgRole,
} from 'chat-shared'
import { api, ApiError } from '../api/client.js'
import { AdminCard, AdminCardFooter, AdminTableEmpty, adminTableSx } from './AdminUi.js'
import { OrgApiKeysSection } from './OrgApiKeysSection.js'
import { OrgTeamsSection } from './OrgTeamsSection.js'
import { OrgSlackSection } from './OrgSlackSection.js'

// Unterbereiche der Organisationsverwaltung — erscheinen im Admin-Menü als
// Einträge unterhalb der aufgeklappten Organisation (AdminLayout) und sind
// einzeln über die Route organizations/:orgId/:section erreichbar.
export const ORGANIZATION_SECTIONS = [
  { path: 'general', label: 'Allgemein' },
  { path: 'members', label: 'Mitglieder' },
  { path: 'groups', label: 'Gruppen' },
  { path: 'mcp-servers', label: 'MCP-Server' },
  { path: 'api-keys', label: 'API-Schlüssel' },
  { path: 'teams', label: 'Microsoft Teams' },
  { path: 'slack', label: 'Slack' },
] as const

export type OrganizationSection = (typeof ORGANIZATION_SECTIONS)[number]['path']

export const isOrganizationSection = (value: string): value is OrganizationSection =>
  ORGANIZATION_SECTIONS.some((section) => section.path === value)

const roleLabel: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Mitglied',
}

const statusColor: Record<McpServerStatus, 'default' | 'success' | 'warning' | 'error'> = {
  unknown: 'default',
  connecting: 'warning',
  connected: 'success',
  disconnected: 'warning',
  unauthorized: 'error',
  error: 'error',
}

const statusLabel: Record<McpServerStatus, string> = {
  unknown: 'unbekannt',
  connecting: 'verbindet …',
  connected: 'verbunden',
  disconnected: 'getrennt',
  unauthorized: 'Auth-Fehler',
  error: 'Fehler',
}

const errorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof ApiError) {
    if (err.message === 'user_not_found') return 'Kein Nutzer mit dieser E-Mail-Adresse gefunden.'
    if (err.message === 'already_member') return 'Dieser Nutzer ist bereits Mitglied.'
    if (err.message === 'not_an_org_member') {
      return 'Dieser Nutzer muss zuerst Mitglied der Organisation werden.'
    }
    if (err.message === 'forbidden') return 'Dafür fehlt dir die Berechtigung.'
    if (err.message === 'invalid_body') return 'Bitte alle Felder korrekt ausfüllen.'
    if (err.message === 'email_taken') return 'Diese E-Mail-Adresse wird bereits verwendet.'
  }
  return fallback
}

type OrganizationManagerProps = {
  orgId: string
  orgName: string
  // Welcher Unterbereich angezeigt wird (siehe ORGANIZATION_SECTIONS).
  section: OrganizationSection
  // Owner/Admin der Organisation ODER ein Systemadmin — darf Mitglieder,
  // Gruppen, Rollen und den Org-Namen verwalten (Server: canManage bzw.
  // resolveEffectiveOrgRole).
  canManage: boolean
  // Nur Systemadmins — Organisationen löschen läuft über den Admin-Endpoint.
  canDelete: boolean
  // Nur Systemadmins — schaltet die Org-Admin-Funktionen (ORG_FEATURES) frei.
  canManageFeatures: boolean
  // Aktuell freigeschaltete Funktionen der Organisation.
  enabledFeatures: Array<OrgFeature>
  onRenamed: (name: string) => void
  onFeaturesChanged?: () => void
  onDeleted: () => void
}

// Verwaltung einer einzelnen Organisation, gegliedert in die Unterbereiche
// aus ORGANIZATION_SECTIONS (Allgemein, Mitglieder, Gruppen, MCP-Server,
// API-Schlüssel, Microsoft Teams, Slack) — pro Aufruf wird genau ein Bereich
// gerendert. Ausschließlich im Einstellungsbereich genutzt (siehe
// pages/admin/AdminOrganizationDetail.tsx), da Organisationen nur dort
// erreichbar sind.
export const OrganizationManager = ({
  orgId,
  orgName,
  section,
  canManage,
  canDelete,
  canManageFeatures,
  enabledFeatures,
  onRenamed,
  onFeaturesChanged,
  onDeleted,
}: OrganizationManagerProps) => {
  const [error, setError] = useState<string | undefined>(undefined)
  const [renameValue, setRenameValue] = useState(orgName)
  const [confirmDeleteOrg, setConfirmDeleteOrg] = useState(false)
  const [featureSaving, setFeatureSaving] = useState(false)

  const [members, setMembers] = useState<Array<OrganizationMember>>([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<OrgRole>('member')

  const [groups, setGroups] = useState<Array<Group>>([])
  const [groupName, setGroupName] = useState('')
  const [expandedGroupId, setExpandedGroupId] = useState<string | undefined>(undefined)
  const [groupMembers, setGroupMembers] = useState<Array<GroupMember>>([])
  const [groupMemberEmail, setGroupMemberEmail] = useState('')

  const [mcpServers, setMcpServers] = useState<Array<McpServerSummary>>([])
  const [mcpName, setMcpName] = useState('')
  const [mcpUrl, setMcpUrl] = useState('')
  const [mcpTransport, setMcpTransport] = useState<McpTransport>('http')
  const [mcpAuthMode, setMcpAuthMode] = useState<McpAuthMode>('none')
  const [mcpAuthToken, setMcpAuthToken] = useState('')
  const [mcpAccess, setMcpAccess] = useState<AssignmentAccess>('write')
  const [deleteMcpTarget, setDeleteMcpTarget] = useState<McpServerSummary | undefined>(undefined)
  const [testResult, setTestResult] = useState<
    { server: McpServerSummary; tools: Array<{ name: string; description?: string }> } | undefined
  >(undefined)

  const reloadMcpServers = () => {
    api.listOrgMcpServers(orgId).then(setMcpServers).catch(() => setMcpServers([]))
  }

  // Nur die Daten des angezeigten Unterbereichs laden.
  useEffect(() => {
    setError(undefined)
    if (section === 'general') setRenameValue(orgName)
    if (section === 'members') api.listOrgMembers(orgId).then(setMembers).catch(() => setMembers([]))
    if (section === 'groups') {
      api.listGroups(orgId).then(setGroups).catch(() => setGroups([]))
      setExpandedGroupId(undefined)
      setGroupMembers([])
    }
    if (section === 'mcp-servers') reloadMcpServers()
  }, [orgId, orgName, section])

  useEffect(() => {
    if (expandedGroupId) {
      api.listGroupMembers(orgId, expandedGroupId).then(setGroupMembers).catch(() => setGroupMembers([]))
    }
  }, [orgId, expandedGroupId])

  const handleRename = async () => {
    setError(undefined)
    try {
      await api.renameOrganization(orgId, renameValue)
      onRenamed(renameValue)
    } catch (err) {
      setError(errorMessage(err, 'Umbenennen fehlgeschlagen'))
    }
  }

  const handleDelete = async () => {
    await api.deleteOrganizationAsAdmin(orgId)
    setConfirmDeleteOrg(false)
    onDeleted()
  }

  // Feature-Freischaltung (nur Systemadmins): vollständige Ersetzung der
  // Liste; danach die Organisationsliste (und damit die Navigation) neu laden.
  const toggleFeature = async (feature: OrgFeature) => {
    setError(undefined)
    setFeatureSaving(true)
    const next = enabledFeatures.includes(feature)
      ? enabledFeatures.filter((entry) => entry !== feature)
      : [...enabledFeatures, feature]
    try {
      await api.updateAdminOrgFeatures(orgId, next)
      onFeaturesChanged?.()
    } catch (err) {
      setError(errorMessage(err, 'Feature-Freischaltung fehlgeschlagen'))
    } finally {
      setFeatureSaving(false)
    }
  }

  const handleInvite = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    try {
      const member = await api.inviteOrgMember(orgId, inviteEmail, inviteRole)
      setMembers((prev) => [...prev, member])
      setInviteEmail('')
    } catch (err) {
      setError(errorMessage(err, 'Einladen fehlgeschlagen'))
    }
  }

  const handleRoleChange = async (userId: string, role: OrgRole) => {
    await api.updateOrgMemberRole(orgId, userId, role)
    setMembers((prev) => prev.map((member) => (member.userId === userId ? { ...member, role } : member)))
  }

  const handleRemoveMember = async (userId: string) => {
    await api.removeOrgMember(orgId, userId)
    setMembers((prev) => prev.filter((member) => member.userId !== userId))
  }

  const handleCreateGroup = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    try {
      const group = await api.createGroup(orgId, groupName)
      setGroups((prev) => [...prev, group])
      setGroupName('')
    } catch (err) {
      setError(errorMessage(err, 'Gruppe anlegen fehlgeschlagen'))
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    await api.deleteGroup(orgId, groupId)
    setGroups((prev) => prev.filter((group) => group.id !== groupId))
    if (expandedGroupId === groupId) setExpandedGroupId(undefined)
  }

  const handleAddGroupMember = async (event: FormEvent) => {
    event.preventDefault()
    if (!expandedGroupId) return
    setError(undefined)
    try {
      const member = await api.addGroupMember(orgId, expandedGroupId, groupMemberEmail)
      setGroupMembers((prev) => [...prev, member])
      setGroupMemberEmail('')
    } catch (err) {
      setError(errorMessage(err, 'Hinzufügen fehlgeschlagen'))
    }
  }

  const handleRemoveGroupMember = async (userId: string) => {
    if (!expandedGroupId) return
    await api.removeGroupMember(orgId, expandedGroupId, userId)
    setGroupMembers((prev) => prev.filter((member) => member.userId !== userId))
  }

  const handleCreateMcpServer = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    try {
      await api.createOrgMcpServer(orgId, {
        name: mcpName,
        url: mcpUrl,
        transport: mcpTransport,
        authMode: mcpAuthMode,
        authToken: mcpAuthMode === 'bearer' && mcpAuthToken.length > 0 ? mcpAuthToken : undefined,
        enabled: true,
        access: mcpAccess,
      })
      setMcpName('')
      setMcpUrl('')
      setMcpAuthToken('')
      setMcpAuthMode('none')
      reloadMcpServers()
    } catch (err) {
      setError(errorMessage(err, 'MCP-Server anlegen fehlgeschlagen'))
    }
  }

  const toggleMcpEnabled = async (server: McpServerSummary) => {
    await api.updateOrgMcpServer(orgId, server.id, { enabled: !server.enabled })
    reloadMcpServers()
  }

  const handleDeleteMcpServer = async () => {
    if (!deleteMcpTarget) return
    await api.deleteOrgMcpServer(orgId, deleteMcpTarget.id)
    setDeleteMcpTarget(undefined)
    reloadMcpServers()
  }

  const handleTestMcpServer = async (server: McpServerSummary) => {
    try {
      const result = await api.testOrgMcpServer(orgId, server.id)
      setTestResult({ server, tools: result.tools })
    } catch (err) {
      setError(errorMessage(err, 'Verbindungstest fehlgeschlagen'))
    }
  }

  const handleAuthorizeMcpServer = async (server: McpServerSummary) => {
    setError(undefined)
    try {
      const { authorizationUrl } = await api.startOrgMcpOAuth(orgId, server.id)
      if (authorizationUrl) {
        window.location.href = authorizationUrl
      } else {
        reloadMcpServers()
      }
    } catch (err) {
      setError(errorMessage(err, 'OAuth-Start fehlgeschlagen'))
    }
  }

  return (
    <Stack gap={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      {section === 'general' && (
        <AdminCard
          title="Allgemein"
          description="Anzeigename der Organisation."
          padded
        >
          {canManage ? (
            <Box
              component="form"
              onSubmit={(event: FormEvent) => {
                event.preventDefault()
                void handleRename()
              }}
              display="flex"
              gap={1}
              alignItems="center"
              maxWidth={480}
            >
              <TextField
                size="small"
                label="Name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                required
                fullWidth
              />
              <Button type="submit" variant="contained">
                Speichern
              </Button>
            </Box>
          ) : (
            <Typography>{orgName}</Typography>
          )}
        </AdminCard>
      )}

      {section === 'general' && canManageFeatures && (
        <AdminCard
          title="Freigeschaltete Funktionen"
          description="Welche Verwaltungs-Funktionen die Owner und Admins dieser Organisation selbst nutzen dürfen. Ohne Freischaltung sind die Bereiche nur für System-Admins zugänglich."
          padded
        >
          <Stack gap={0.5} maxWidth={480}>
            {ORG_FEATURES.map((feature) => (
              <Box key={feature} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">{ORG_FEATURE_LABELS[feature]}</Typography>
                <Switch
                  size="small"
                  checked={enabledFeatures.includes(feature)}
                  disabled={featureSaving}
                  onChange={() => void toggleFeature(feature)}
                />
              </Box>
            ))}
          </Stack>
        </AdminCard>
      )}

      {section === 'general' && canDelete && (
        <AdminCard
          title="Organisation löschen"
          description="Entfernt die Organisation samt Mitgliedschaften, Gruppen und Zuweisungen — das kann nicht rückgängig gemacht werden."
          padded
          sx={{ borderColor: 'error.light' }}
        >
          <Button color="error" variant="outlined" onClick={() => setConfirmDeleteOrg(true)}>
            Organisation löschen
          </Button>
        </AdminCard>
      )}

      {section === 'members' && (
      <AdminCard title={`Mitglieder (${members.length})`}>
        <List dense sx={{ py: 0.5 }}>
          {members.length === 0 && (
            <Typography variant="body2" color="text.secondary" px={2} py={2} textAlign="center">
              Noch keine Mitglieder.
            </Typography>
          )}
          {members.map((member) => (
            <ListItem
              key={member.userId}
              secondaryAction={
                canManage && member.role !== 'owner' ? (
                  <Stack direction="row" gap={1} alignItems="center">
                    <Select
                      size="small"
                      value={member.role}
                      onChange={(e) => void handleRoleChange(member.userId, e.target.value as OrgRole)}
                      disabled={!canManage}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="member">Mitglied</MenuItem>
                    </Select>
                    <IconButton size="small" onClick={() => void handleRemoveMember(member.userId)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <Chip size="small" label={roleLabel[member.role]} />
                )
              }
            >
              <ListItemText primary={member.displayName} secondary={member.email} />
            </ListItem>
          ))}
        </List>
        {canManage && (
          <AdminCardFooter>
            <Box component="form" onSubmit={handleInvite} display="flex" gap={1}>
              <TextField
                size="small"
                type="email"
                label="E-Mail-Adresse"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                fullWidth
              />
              <Select size="small" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as OrgRole)}>
                <MenuItem value="member">Mitglied</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
              <Button type="submit" variant="contained">
                Einladen
              </Button>
            </Box>
          </AdminCardFooter>
        )}
      </AdminCard>
      )}

      {section === 'groups' && (
      <AdminCard title={`Gruppen (${groups.length})`}>
        <List dense sx={{ py: 0.5 }}>
          {groups.map((group, index) => (
            <Box key={group.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                secondaryAction={
                  canManage ? (
                    <IconButton size="small" onClick={() => void handleDeleteGroup(group.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : undefined
                }
                disablePadding
              >
                <ListItemButton
                  onClick={() => setExpandedGroupId(expandedGroupId === group.id ? undefined : group.id)}
                >
                  <GroupIcon fontSize="small" sx={{ mr: 1 }} />
                  <ListItemText primary={group.name} />
                </ListItemButton>
              </ListItem>
              {expandedGroupId === group.id && (
                <Box pl={4} pr={2} pb={2}>
                  <List dense disablePadding>
                    {groupMembers.map((member) => (
                      <ListItem
                        key={member.userId}
                        secondaryAction={
                          canManage ? (
                            <IconButton size="small" onClick={() => void handleRemoveGroupMember(member.userId)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ) : undefined
                        }
                      >
                        <ListItemText primary={member.displayName} secondary={member.email} />
                      </ListItem>
                    ))}
                    {groupMembers.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Noch keine Mitglieder.
                      </Typography>
                    )}
                  </List>
                  {canManage && (
                    <Box component="form" onSubmit={handleAddGroupMember} display="flex" gap={1} mt={1}>
                      <TextField
                        size="small"
                        type="email"
                        label="E-Mail-Adresse (Org-Mitglied)"
                        value={groupMemberEmail}
                        onChange={(e) => setGroupMemberEmail(e.target.value)}
                        required
                        fullWidth
                      />
                      <Button type="submit" variant="contained" size="small">
                        Hinzufügen
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
          {groups.length === 0 && (
            <Typography variant="body2" color="text.secondary" px={2} py={2} textAlign="center">
              Noch keine Gruppen.
            </Typography>
          )}
        </List>
        {canManage && (
          <AdminCardFooter>
            <Box component="form" onSubmit={handleCreateGroup} display="flex" gap={1}>
              <TextField
                size="small"
                label="Neue Gruppe"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                fullWidth
              />
              <Button type="submit" variant="contained">
                Anlegen
              </Button>
            </Box>
          </AdminCardFooter>
        )}
      </AdminCard>
      )}

      {section === 'mcp-servers' && (
      <AdminCard title={`MCP-Server (${mcpServers.length})`}>
        <Table size="small" sx={adminTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Zugriff</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {mcpServers.map((server) => (
              <TableRow key={server.id}>
                <TableCell>{server.name}</TableCell>
                <TableCell>{server.url}</TableCell>
                <TableCell>
                  <Tooltip title={server.lastError ?? ''}>
                    <Chip
                      size="small"
                      label={statusLabel[server.status]}
                      color={statusColor[server.status]}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>{server.access === 'write' ? 'Lesen & Schreiben' : 'Nur Lesen'}</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={server.enabled}
                    disabled={!server.canManage}
                    onChange={() => void toggleMcpEnabled(server)}
                  />
                </TableCell>
                <TableCell>
                  {server.authMode === 'oauth' && server.status !== 'connected' && server.canManage && (
                    <IconButton size="small" onClick={() => void handleAuthorizeMcpServer(server)} sx={{ mr: 1 }}>
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Button size="small" onClick={() => void handleTestMcpServer(server)} sx={{ mr: 1 }}>
                    Testen
                  </Button>
                  {server.canManage && (
                    <IconButton size="small" onClick={() => setDeleteMcpTarget(server)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {mcpServers.length === 0 && (
              <AdminTableEmpty colSpan={6}>
                Noch keine MCP-Server für diese Organisation.
              </AdminTableEmpty>
            )}
          </TableBody>
        </Table>
      </AdminCard>
      )}

      {section === 'mcp-servers' && canManage && (
        <AdminCard
          title="Server hinzufügen"
          description="Externe Tool-Server über das Model Context Protocol anbinden."
          padded
        >
          <Box component="form" onSubmit={handleCreateMcpServer} display="flex" flexDirection="column" gap={2} maxWidth={480}>
            <TextField label="Name" size="small" value={mcpName} onChange={(e) => setMcpName(e.target.value)} required />
            <TextField
              label="URL / Befehl"
              size="small"
              value={mcpUrl}
              onChange={(e) => setMcpUrl(e.target.value)}
              required
              helperText="Bei stdio: Kommandozeile des lokalen MCP-Servers"
            />
            <Select
              size="small"
              value={mcpTransport}
              onChange={(e) => {
                if (e.target.value === 'http' || e.target.value === 'stdio') setMcpTransport(e.target.value)
              }}
            >
              <MenuItem value="http">http</MenuItem>
              <MenuItem value="stdio">stdio</MenuItem>
            </Select>
            <Select
              size="small"
              value={mcpAuthMode}
              onChange={(e) => {
                if (e.target.value === 'none' || e.target.value === 'bearer' || e.target.value === 'oauth') {
                  setMcpAuthMode(e.target.value)
                }
              }}
            >
              <MenuItem value="none">Kein Auth</MenuItem>
              <MenuItem value="bearer">Bearer-Token</MenuItem>
              <MenuItem value="oauth">OAuth</MenuItem>
            </Select>
            {mcpAuthMode === 'bearer' && (
              <TextField
                label="Auth-Token"
                size="small"
                type="password"
                value={mcpAuthToken}
                onChange={(e) => setMcpAuthToken(e.target.value)}
                required
              />
            )}
            <Select size="small" value={mcpAccess} onChange={(e) => setMcpAccess(e.target.value as AssignmentAccess)}>
              <MenuItem value="write">Mitglieder: Lesen & Schreiben</MenuItem>
              <MenuItem value="read">Mitglieder: Nur Lesen</MenuItem>
            </Select>
            <Box>
              <Button type="submit" variant="contained">
                Hinzufügen
              </Button>
            </Box>
          </Box>
        </AdminCard>
      )}

      {/* Nur Owner/Admin: der Server beantwortet die Endpunkte für Mitglieder mit 403. */}
      {section === 'api-keys' && canManage && <OrgApiKeysSection orgId={orgId} />}
      {section === 'teams' && canManage && <OrgTeamsSection orgId={orgId} />}
      {section === 'slack' && canManage && <OrgSlackSection orgId={orgId} />}

      <Dialog open={confirmDeleteOrg} onClose={() => setConfirmDeleteOrg(false)}>
        <DialogTitle>Organisation löschen?</DialogTitle>
        <DialogContent>
          „{orgName}“ wird mit allen Mitgliedschaften, Gruppen und Zuweisungen dauerhaft entfernt.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOrg(false)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDelete()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteMcpTarget)} onClose={() => setDeleteMcpTarget(undefined)}>
        <DialogTitle>Server löschen?</DialogTitle>
        <DialogContent>
          „{deleteMcpTarget?.name}“ wird entfernt und die zugehörigen Tools stehen nicht mehr zur Verfügung.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMcpTarget(undefined)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDeleteMcpServer()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(testResult)} onClose={() => setTestResult(undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Gefundene Tools: {testResult?.server.name}</DialogTitle>
        <DialogContent>
          {testResult?.tools.length === 0 && <Typography>Keine Tools gefunden.</Typography>}
          {testResult?.tools.map((tool) => (
            <Box key={tool.name} mb={1}>
              <Typography variant="subtitle2">{tool.name}</Typography>
              {tool.description && (
                <Typography variant="body2" color="text.secondary">
                  {tool.description}
                </Typography>
              )}
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Stack>
  )
}
